import { h } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { Variant } from './addToCartData';

interface VariantSelectorProps {
  /** Instance-unique id prefix, so several selectors can live on one page (ORT-3055) */
  id: string;
  variants: Variant[];
  selectedONumber: string | null;
  onSelect: (variant: Variant) => void;
}

/**
 * Dosage form / pack size dropdown built from radios (ported from the vanilla add-to-cart.js, ORT-3112 styles).
 * Collapsed, only the checked label is visible (CSS); `show` reveals all options.
 * Pointer clicks toggle the list, keyboard arrows change the selection without closing it,
 * Enter/Escape close it and return focus to the toggle.
 *
 * Placement mirrors a native <select>: the list opens downward when it fits below the row inside the
 * nearest clipping ancestor, otherwise upward, and is capped to the room on that side. In the mobile
 * bottom sheet (ORT-3055) the clipping ancestor is the scrolling .modal-body with only the button row
 * below the dropdown, so the list flips up over the packshot/title block; on desktop the ancestor is the
 * full-viewport dialog and the list keeps opening downward.
 */

const PANEL_GAP = 8; // breathing room to the clipping edge
const MIN_ROWS_VISIBLE = 2;
const ROW_HEIGHT = 56;

/** Nearest ancestor that clips or scrolls its overflow (Bootstrap's .modal-body / .modal), else the viewport. */
const clippingRect = (element: HTMLElement): { top: number; bottom: number } => {
  let node = element.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden' || overflowY === 'clip') {
      return node.getBoundingClientRect();
    }
    node = node.parentElement;
  }
  return { top: 0, bottom: window.innerHeight };
};

export const VariantSelector = ({ id, variants, selectedONumber, onSelect }: VariantSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [panelMaxHeight, setPanelMaxHeight] = useState<number | null>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const pointerInteraction = useRef(false);

  // Decide the opening direction before the open list paints (and again on resize/orientation change).
  useLayoutEffect(() => {
    const panel = optionsRef.current;
    const row = panel?.parentElement;
    if (!open || !panel || !row) {
      setDropUp(false);
      setPanelMaxHeight(null);
      return;
    }
    const place = () => {
      // Stylesheet cap (526px page / 340px modal): read it without the inline override from a previous run
      panel.style.maxHeight = '';
      const cap = parseFloat(getComputedStyle(panel).maxHeight) || Number.POSITIVE_INFINITY;
      const wanted = Math.min(panel.scrollHeight + panel.offsetHeight - panel.clientHeight, cap); // content + borders
      const clip = clippingRect(row);
      const rowRect = row.getBoundingClientRect();
      const below = clip.bottom - rowRect.top - PANEL_GAP; // opening down covers the row, so measure from its top
      const above = rowRect.bottom - clip.top - PANEL_GAP; // opening up ends at the row's bottom edge
      const up = wanted > below && above > below;
      const room = Math.max(up ? above : below, MIN_ROWS_VISIBLE * ROW_HEIGHT);
      setDropUp(up);
      setPanelMaxHeight(Math.min(cap, room));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  const titleId = `${id}-title`;
  const optionsId = `${id}-options`;
  const radioName = `${id}-radio`;

  const handleToggleClick = (event: MouseEvent) => {
    const willOpen = !open;
    setOpen(willOpen);
    // keyboard activation (detail === 0): move focus to the checked option
    if (event.detail === 0 && willOpen) {
      requestAnimationFrame(() => optionsRef.current?.querySelector<HTMLInputElement>('input:checked')?.focus());
    }
  };

  // Pointer click on the already checked option: nothing changes, so no change event follows; toggle the list here.
  // Clicking ANOTHER option must not touch state in the click handler: Preact would re-render before the browser
  // dispatches `change`, sync the still-old `checked` prop back into the DOM, and Chrome/WebKit then drop the
  // change event (they compare the DOM state after the click with the one before it). The list closes in
  // handleChange instead, after onSelect. Firefox fires change regardless, which hid the bug there.
  const handleRadioClick = (variant: Variant) => {
    if (!pointerInteraction.current || variant.packsize.oNumber !== selectedONumber) return;
    pointerInteraction.current = false;
    setOpen((prev) => !prev);
  };

  const handleChange = (variant: Variant) => {
    onSelect(variant);
    if (!pointerInteraction.current) return; // keyboard arrows change the selection without closing
    pointerInteraction.current = false;
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    pointerInteraction.current = false; // a pointerdown without a click (drag away) must not count for the next key
    if (!open || (event.key !== 'Escape' && event.key !== 'Enter')) return;
    event.preventDefault();
    setOpen(false);
    toggleRef.current?.focus();
  };

  return (
    <>
      <span className="product-variant-selection-label ps-3 mb-2" id={titleId}>
        Darreichungsform / Packungsgröße
      </span>
      <div className="product-variant-selection-sizes d-flex position-relative mb-3">
        <button
          type="button"
          className="btn"
          ref={toggleRef}
          aria-controls={optionsId}
          aria-expanded={open}
          onClick={handleToggleClick}
        >
          <span className="visually-hidden">Optionen anzeigen</span>
        </button>
        <div
          id={optionsId}
          ref={optionsRef}
          className={`product-variant-selection-sizes-inner position-absolute z-1 d-flex flex-column w-100${open ? ' show' : ''}${dropUp ? ' drop-up' : ''}`}
          style={panelMaxHeight !== null ? { maxHeight: `${panelMaxHeight}px` } : undefined}
          role="radiogroup"
          aria-labelledby={titleId}
          onKeyDown={handleKeyDown}
        >
          {variants.map((variant, index) => {
            // Figma option-dosage: thumbnail + two lines (dosage form bold, size regular).
            // Thumbnail is the pack size's own PackSizeVisual only, no fallback: an empty box until the PIM has it.
            const thumbnail = variant.packsize.packsizeVisualUrl || null;
            return (
              <label
                key={variant.packsize.oNumber}
                className="product-variant-selection-option align-items-center"
                onPointerDown={() => {
                  pointerInteraction.current = true;
                }}
              >
                <input
                  type="radio"
                  name={radioName}
                  id={`${id}-option-${index}`}
                  className="visually-hidden"
                  checked={variant.packsize.oNumber === selectedONumber}
                  onChange={() => handleChange(variant)}
                  onClick={() => handleRadioClick(variant)}
                />
                <span className="product-variant-selection-option-thumbnail" aria-hidden="true">
                  {thumbnail && <img src={thumbnail} alt="" />}
                </span>
                <span className="product-variant-selection-option-text">
                  <strong>{variant.dosageForm.name}</strong>
                  <span>{variant.packsize.size}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
};
